// app/frontend/components/layouts/HybridLayout.tsx
import React from "react";
import AuthenticatedLayout from "./AuthenticatedLayout";
import GuestLayout from "./GuestLayout";
import { useUser } from "../../context/UserContext";

type Props = {
  children: React.ReactNode;
  /**
   * Optional escape hatch: render GuestLayout even if authenticated.
   * Useful for special marketing pages you never want inside the app chrome.
   */
  forceGuest?: boolean;
  /**
   * Enable scroll-away header behavior on mobile
   * Header will hide when scrolling down and show when scrolling up
   */
  hideOnScroll?: boolean;
};

export default function HybridLayout({
  children,
  forceGuest = false,
  hideOnScroll = false,
}: Props) {
  const { isAuthenticated } = useUser();

  // Store the hideOnScroll prop in a ref to track changes
  const hideOnScrollRef = React.useRef(hideOnScroll);
  React.useEffect(() => {
    hideOnScrollRef.current = hideOnScroll;
  }, [hideOnScroll]);

  // Debug logging - log on every render, not just on effect
  console.log("[HybridLayout] Render - hideOnScroll prop:", hideOnScroll);
  console.log(
    "[HybridLayout] Render - hideOnScroll ref:",
    hideOnScrollRef.current
  );
  console.log("[HybridLayout] Render - props received:", {
    hideOnScroll,
    forceGuest,
  });

  // Log stack trace to see where this is being called from
  console.trace("[HybridLayout] Render stack trace");

  // Debug logging
  React.useEffect(() => {
    console.log("[HybridLayout] Effect - hideOnScroll:", hideOnScroll);
    console.log("[HybridLayout] Effect - isAuthenticated:", isAuthenticated);
    console.log("[HybridLayout] Effect - forceGuest:", forceGuest);
    console.log(
      "[HybridLayout] Effect - will use:",
      !forceGuest && isAuthenticated ? "AuthenticatedLayout" : "GuestLayout"
    );
  }, [hideOnScroll, isAuthenticated, forceGuest]);

  // Determine which layout to use
  const useAuthenticatedLayout = !forceGuest && isAuthenticated;

  // Use key to ensure proper remounting when switching layouts
  // Don't include hideOnScroll in key to avoid unnecessary remounts
  const layoutKey = useAuthenticatedLayout ? "authenticated" : "guest";

  console.log("[HybridLayout] Rendering with:", {
    useAuthenticatedLayout,
    hideOnScroll,
    layoutKey,
  });

  if (useAuthenticatedLayout) {
    return (
      <AuthenticatedLayout key={layoutKey} hideOnScroll={hideOnScroll}>
        {children}
      </AuthenticatedLayout>
    );
  }

  return (
    <GuestLayout key={layoutKey} hideOnScroll={hideOnScroll}>
      {children}
    </GuestLayout>
  );
}
